import { useEffect } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { useRoutingStore } from '@/stores/routingStore';
import type { Route } from '@/types';

export function MapLayers() {
  const { map, isLoaded, userLocation } = useMapStore();
  const { routes, selectedRouteIndex, navigation } = useRoutingStore();

  // Route layer
  useRouteLayer(map, isLoaded, routes, selectedRouteIndex);

  // Navigation layer — while navigating, the drawn line is trimmed to start at the
  // user's current position, so the already-travelled part behind them disappears.
  useNavigationLayer(
    map,
    isLoaded,
    navigation.isActive ? navigation.route : null,
    navigation.isActive ? userLocation : null
  );

  return null;
}

// trimRouteToUser drops the portion of a route polyline the user has already
// passed: it finds the nearest point on the line to the user and returns that
// point plus everything ahead of it. Projection is done in lng/lat space, which
// is accurate enough over the short segments of a road geometry.
function trimRouteToUser(
  coords: number[][],
  user: { lng: number; lat: number }
): number[][] {
  if (coords.length < 2) return coords;
  let bestI = 0;
  let bestPx = coords[0][0];
  let bestPy = coords[0][1];
  let bestD = Infinity;
  for (let i = 0; i < coords.length - 1; i++) {
    const ax = coords[i][0];
    const ay = coords[i][1];
    const dx = coords[i + 1][0] - ax;
    const dy = coords[i + 1][1] - ay;
    const len2 = dx * dx + dy * dy || 1e-12;
    let t = ((user.lng - ax) * dx + (user.lat - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const px = ax + t * dx;
    const py = ay + t * dy;
    const ddx = user.lng - px;
    const ddy = user.lat - py;
    const d = ddx * ddx + ddy * ddy;
    if (d < bestD) {
      bestD = d;
      bestI = i;
      bestPx = px;
      bestPy = py;
    }
  }
  return [[bestPx, bestPy], ...coords.slice(bestI + 1)];
}

function useRouteLayer(
  map: maplibregl.Map | null,
  isLoaded: boolean,
  routes: Route[],
  selectedRouteIndex: number
) {
  useEffect(() => {
    if (!map || !isLoaded) return;

    // Ensure route sources exist
    if (!map.getSource('routes-alternative')) {
      map.addSource('routes-alternative', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!map.getSource('routes-selected')) {
      map.addSource('routes-selected', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Add alternative routes layer (behind selected)
    if (!map.getLayer('routes-alternative-line')) {
      map.addLayer({
        id: 'routes-alternative-line',
        type: 'line',
        source: 'routes-alternative',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#9aa0a6',
          'line-width': 6,
          'line-opacity': 0.6,
        },
      });
    }

    // Add selected route layer
    if (!map.getLayer('routes-selected-outline')) {
      map.addLayer({
        id: 'routes-selected-outline',
        type: 'line',
        source: 'routes-selected',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#185abc',
          'line-width': 10,
        },
      });
    }

    if (!map.getLayer('routes-selected-line')) {
      map.addLayer({
        id: 'routes-selected-line',
        type: 'line',
        source: 'routes-selected',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#4285f4',
          'line-width': 6,
        },
      });
    }

    return () => {
      ['routes-alternative-line', 'routes-selected-outline', 'routes-selected-line'].forEach(
        (layerId) => {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        }
      );
      ['routes-alternative', 'routes-selected'].forEach((sourceId) => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
    };
  }, [map, isLoaded]);

  // Update route data
  useEffect(() => {
    if (!map || !isLoaded) return;

    const alternativeSource = map.getSource('routes-alternative') as maplibregl.GeoJSONSource;
    const selectedSource = map.getSource('routes-selected') as maplibregl.GeoJSONSource;

    if (!alternativeSource || !selectedSource) return;

    // Alternative routes
    const alternativeFeatures = routes
      .filter((_, index) => index !== selectedRouteIndex)
      .map((route) => ({
        type: 'Feature' as const,
        geometry: route.geometry,
        properties: { id: route.id },
      }));

    alternativeSource.setData({
      type: 'FeatureCollection',
      features: alternativeFeatures,
    });

    // Selected route
    const selectedRoute = routes[selectedRouteIndex];
    selectedSource.setData({
      type: 'FeatureCollection',
      features: selectedRoute
        ? [{
            type: 'Feature' as const,
            geometry: selectedRoute.geometry,
            properties: { id: selectedRoute.id },
          }]
        : [],
    });
  }, [map, isLoaded, routes, selectedRouteIndex]);
}

function useNavigationLayer(
  map: maplibregl.Map | null,
  isLoaded: boolean,
  activeRoute: Route | null,
  userLocation: { lng: number; lat: number } | null
) {
  useEffect(() => {
    if (!map || !isLoaded) return;

    // Add navigation route source
    if (!map.getSource('navigation-route')) {
      map.addSource('navigation-route', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Add navigation route layers
    if (!map.getLayer('navigation-route-outline')) {
      map.addLayer({
        id: 'navigation-route-outline',
        type: 'line',
        source: 'navigation-route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#137333',
          'line-width': 12,
        },
      });
    }

    if (!map.getLayer('navigation-route-line')) {
      map.addLayer({
        id: 'navigation-route-line',
        type: 'line',
        source: 'navigation-route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#34a853',
          'line-width': 8,
        },
      });
    }

    return () => {
      ['navigation-route-outline', 'navigation-route-line'].forEach((layerId) => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });
      if (map.getSource('navigation-route')) {
        map.removeSource('navigation-route');
      }
    };
  }, [map, isLoaded]);

  // Update navigation route
  useEffect(() => {
    if (!map || !isLoaded) return;

    const source = map.getSource('navigation-route') as maplibregl.GeoJSONSource;
    if (!source) return;

    let geometry = activeRoute?.geometry as
      | { type: string; coordinates: number[][] }
      | undefined;

    // Trim the already-travelled tail so the line only shows from the user's
    // current position forward to the destination.
    if (
      geometry &&
      userLocation &&
      Array.isArray(geometry.coordinates) &&
      geometry.coordinates.length > 1
    ) {
      geometry = {
        type: 'LineString',
        coordinates: trimRouteToUser(geometry.coordinates, userLocation),
      };
    }

    source.setData({
      type: 'FeatureCollection',
      features: geometry
        ? [{ type: 'Feature', geometry: geometry as GeoJSON.Geometry, properties: {} }]
        : [],
    });
  }, [map, isLoaded, activeRoute, userLocation]);
}
